import { useEffect, useRef, useState } from "react";
import { getTickets, getRooms, updateTicketStatus, getMessages, sendMessage } from "../../firebase/services";
import { Badge, TicketStatusBadge, SectionTitle, EmptyState, Spinner, Modal, Btn } from "../../components/UI";

const CATEGORY_ICONS = { Plomberie:"🔧", "Électricité":"⚡", "Serrure / porte":"🔑", Climatisation:"❄️", Autre:"⚙️" };

function timeAgo(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 3600) return `il y a ${Math.round(s/60)} min`;
  if (s < 86400) return `il y a ${Math.round(s/3600)}h`;
  return `il y a ${Math.round(s/86400)}j`;
}

function formatTime(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString("fr-FR", { hour:"2-digit", minute:"2-digit" });
}

// ── Composant de réponse rapide dans la modal ──────────────
function TicketChat({ roomId, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!roomId) return;
    const unsub = getMessages(roomId, setMessages);
    return unsub;
  }, [roomId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior:"smooth" });
  }, [messages]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending || !roomId) return;
    setSending(true);
    setText("");
    await sendMessage(roomId, { text: trimmed, senderId: currentUserId, senderRole: "owner" });
    setSending(false);
  };

  if (!roomId) return (
    <div style={{ background:"#fff5f5", borderRadius:10, padding:"10px 14px", fontSize:12, color:"#a32d2d", marginTop:12 }}>
      ⚠️ Ce ticket n'est lié à aucune chambre — impossible d'envoyer un message.
    </div>
  );

  const textMessages = messages.filter(m => m.type === "text" || !m.type);

  return (
    <div style={{ marginTop:12 }}>
      <p style={{ fontSize:12, fontWeight:700, color:"#4a6a8a", marginBottom:6 }}>💬 Conversation avec l'étudiant</p>

      {/* Historique des messages texte */}
      <div style={{ maxHeight:160, overflowY:"auto", background:"#f5f8fc", borderRadius:10, padding:"8px 10px", marginBottom:8, border:"0.5px solid #d0dce8" }}>
        {textMessages.length === 0 && (
          <p style={{ fontSize:12, color:"#7a90a8", textAlign:"center", padding:"10px 0" }}>Aucun message — démarrez la conversation</p>
        )}
        {textMessages.map(msg => {
          const own = msg.senderId === currentUserId || msg.senderRole === "owner";
          return (
            <div key={msg.id} style={{ display:"flex", justifyContent: own ? "flex-end" : "flex-start", marginBottom:5 }}>
              <div style={{ maxWidth:"80%" }}>
                <div style={{
                  padding:"7px 11px", borderRadius:12, fontSize:12, lineHeight:1.4,
                  background: own ? "#1a3a5c" : "white",
                  color: own ? "white" : "#1a2a3a",
                  border: own ? "none" : "0.5px solid #d0dce8",
                  borderBottomRightRadius: own ? 4 : 12,
                  borderBottomLeftRadius: own ? 12 : 4,
                }}>
                  {msg.text}
                </div>
                <p style={{ fontSize:10, color:"#7a90a8", margin:"2px 2px 0", textAlign: own ? "right" : "left" }}>
                  {own ? "IAM" : "Étudiant"} · {formatTime(msg.createdAt)}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Zone de saisie */}
      <div style={{ display:"flex", gap:8 }}>
        <input
          style={{ flex:1, border:"1.5px solid #d0dce8", borderRadius:22, padding:"9px 13px", fontSize:13, fontFamily:"inherit", background:"white", color:"#1a2a3a", outline:"none" }}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Écrire une réponse..."
          disabled={sending}
        />
        <button
          style={{ width:40, height:40, borderRadius:"50%", background: text.trim() ? "#1a3a5c" : "#d0dce8", color:"white", border:"none", fontSize:15, cursor: text.trim() ? "pointer" : "default", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"background .2s" }}
          onClick={handleSend}
          disabled={!text.trim() || sending}
        >➤</button>
      </div>
    </div>
  );
}

export default function OwnerTickets({ onNavigate, currentUserId }) {
  const [tickets, setTickets] = useState([]);
  const [rooms, setRooms]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState("all");
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const unsubs = [
      getTickets(data => { setTickets(data); setLoading(false); }),
      getRooms(setRooms),
    ];
    return () => unsubs.forEach(u => u());
  }, []);

  const filtered = tickets.filter(t => filter === "all" || t.status === filter);

  const roomName = (id) => {
    const r = rooms.find(r => r.id === id);
    return r ? `Ch. ${r.number} — ${r.tenantName || "?"}` : "Chambre inconnue";
  };

  const handleStatus = async (ticket, newStatus) => {
    setUpdating(true);
    await updateTicketStatus(ticket.id, ticket.roomId, newStatus);
    setUpdating(false);
    // Mettre à jour le ticket sélectionné localement
    setSelected(prev => prev ? { ...prev, status: newStatus } : null);
  };

  const borderColor = { open:"#e24b4a", prog:"#ef9f27", done:"#639922" };

  if (loading) return <div style={styles.page}><Spinner /></div>;

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <h1 style={styles.title}>Tickets</h1>
        <div style={{ display:"flex", gap:6 }}>
          <div style={styles.countBadge}>{tickets.filter(t => t.status === "open").length} ouverts</div>
        </div>
      </div>

      <div style={styles.filterBar}>
        {[["all","Tous"],["open","Ouverts"],["prog","En cours"],["done","Résolus"]].map(([val, label]) => (
          <button
            key={val}
            style={{ ...styles.pill, ...(filter === val ? styles.pillActive : {}) }}
            onClick={() => setFilter(val)}
          >{label}</button>
        ))}
      </div>

      <div style={styles.scroll}>
        {filtered.length === 0 ? (
          <EmptyState icon="🎉" title="Aucun ticket" subtitle={filter === "all" ? "Tout va bien !" : "Aucun ticket dans cette catégorie"} />
        ) : filtered.map(t => (
          <div
            key={t.id}
            style={{ ...styles.ticketCard, borderLeft:`4px solid ${borderColor[t.status] || "#d0dce8"}` }}
            onClick={() => setSelected(t)}
          >
            <div style={styles.ticketHeader}>
              <span style={styles.catIcon}>{CATEGORY_ICONS[t.category] || "⚙️"}</span>
              <div style={{ flex:1 }}>
                <div style={styles.ticketRoom}>{roomName(t.roomId)}</div>
                <div style={styles.ticketCat}>{t.category} · {timeAgo(t.createdAt)}</div>
              </div>
              <TicketStatusBadge status={t.status} />
            </div>
            <p style={styles.ticketDesc}>{t.description}</p>
            {t.status === "done" && t.rating !== null && (
              <p style={styles.rating}>{"★".repeat(t.rating)}{"☆".repeat(5 - t.rating)} — Retour étudiant</p>
            )}
            {t.status !== "done" && (
              <div style={{ display:"flex", gap:8, marginTop:10 }}>
                {t.status === "open" && (
                  <button style={styles.actionBtnPri} onClick={e => { e.stopPropagation(); handleStatus(t, "prog"); }}>
                    ▶ Prendre en charge
                  </button>
                )}
                {t.status === "prog" && (
                  <button style={styles.actionBtnPri} onClick={e => { e.stopPropagation(); handleStatus(t, "done"); }}>
                    ✓ Marquer résolu
                  </button>
                )}
                <button style={styles.actionBtnSec} onClick={e => { e.stopPropagation(); setSelected(t); }}>
                  💬 Répondre
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Modal ticket avec chat intégré */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title={`Ticket — ${selected?.category}`}>
        {selected && (
          <div>
            <p style={{ fontSize:13, color:"#4a6a8a", marginBottom:4 }}>{roomName(selected.roomId)}</p>
            <p style={{ fontSize:14, color:"#1a2a3a", marginBottom:10, lineHeight:1.5 }}>{selected.description}</p>
            {selected.photoUrl && (
              <img src={selected.photoUrl} alt="Photo du problème" style={{ width:"100%", borderRadius:10, marginBottom:10 }} />
            )}
            <div style={{ marginBottom:10 }}>
              <TicketStatusBadge status={selected.status} />
              <span style={{ fontSize:12, color:"#7a90a8", marginLeft:8 }}>{timeAgo(selected.createdAt)}</span>
            </div>

            {/* Boutons de statut */}
            <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:4 }}>
              {selected.status === "open" && (
                <Btn variant="primary" onClick={() => handleStatus(selected, "prog")} disabled={updating}>
                  ▶ Prendre en charge
                </Btn>
              )}
              {selected.status === "prog" && (
                <Btn variant="primary" onClick={() => handleStatus(selected, "done")} disabled={updating}>
                  ✓ Marquer comme résolu
                </Btn>
              )}
            </div>

            {/* Chat intégré directement dans la modal */}
            <TicketChat
              roomId={selected.roomId || null}
              currentUserId={currentUserId || "proprietaire"}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

const styles = {
  page:       { display:"flex", flexDirection:"column", height:"100%", background:"#f5f8fc", fontFamily:"'Barlow', sans-serif" },
  topBar:     { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"16px 16px 8px" },
  title:      { fontSize:22, fontWeight:800, color:"#1a3a5c", fontFamily:"'Barlow Condensed', sans-serif" },
  countBadge: { background:"#fdecea", color:"#a32d2d", borderRadius:20, padding:"4px 10px", fontSize:12, fontWeight:600 },
  filterBar:  { display:"flex", gap:8, padding:"0 16px 12px", overflowX:"auto" },
  pill:       { padding:"6px 14px", borderRadius:20, border:"1.5px solid #d0dce8", background:"white", color:"#5a7a9a", fontSize:12, fontWeight:500, cursor:"pointer", whiteSpace:"nowrap", fontFamily:"inherit" },
  pillActive: { background:"#1a3a5c", color:"white", borderColor:"#1a3a5c" },
  scroll:     { flex:1, overflowY:"auto", padding:"0 16px 80px" },
  ticketCard: { background:"white", borderRadius:"0 12px 12px 0", padding:"12px 14px", marginBottom:10, borderTop:"0.5px solid #d0dce8", borderRight:"0.5px solid #d0dce8", borderBottom:"0.5px solid #d0dce8", cursor:"pointer" },
  ticketHeader:{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:6 },
  catIcon:    { fontSize:20, marginTop:1 },
  ticketRoom: { fontSize:13, fontWeight:600, color:"#1a2a3a" },
  ticketCat:  { fontSize:11, color:"#7a90a8", marginTop:1 },
  ticketDesc: { fontSize:13, color:"#2a4a6a", lineHeight:1.5, margin:0 },
  rating:     { fontSize:12, color:"#8a6a00", marginTop:6 },
  actionBtnPri:{ flex:1, padding:"8px", borderRadius:8, background:"#1a3a5c", color:"white", border:"none", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit" },
  actionBtnSec:{ flex:1, padding:"8px", borderRadius:8, background:"#f0f5fa", color:"#1a3a5c", border:"none", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit" },
};
